import { isNil } from 'lodash-es';

import type {
  Timeseries,
  TimeseriesDatapoint,
} from '../../../types/timeseries.js';
import { getDatapointIsGap } from '../../../utils/get-data-point-is-gap.js';
import {
  SparklineDatapointMetadata,
  SparklineGapPolicy,
} from '../types/sparkline-props.js';
import { SparklineXAxisInternalProps } from '../types/sparkline-x-axis-props.js';

export const sanitizeSparklineData = (
  series: Timeseries,
  gapPolicy: SparklineGapPolicy,
  xAxis: SparklineXAxisInternalProps,
) => {
  let sanitizedDatapoints: SparklineDatapointMetadata[] = [];

  let dataMin = Infinity;
  let dataMax = -Infinity;

  let xAxisMin: Date = series.datapoints[0].start;
  let xAxisMax: Date = series.datapoints[0].start;

  const customXAxisMin = xAxis.min !== 'data-min';
  const customXAxisMax = xAxis.max !== 'data-max';

  const updateDataMin = (value: number) => {
    if (value < dataMin) {
      dataMin = value;
    }
  };

  const updateDataMax = (value: number) => {
    if (value > dataMax) {
      dataMax = value;
    }
  };

  const updateXAxisMin = (value: Date) => {
    if (value < xAxisMin) {
      xAxisMin = value;
    }
  };

  const updateXAxisMax = (value: Date) => {
    if (value > xAxisMax) {
      xAxisMax = value;
    }
  };

  const updateXYBoundaries = (datapoint: TimeseriesDatapoint) => {
    updateDataMin(datapoint.value);
    updateDataMax(datapoint.value);
    if (customXAxisMin) {
      updateXAxisMin(datapoint.start);
    }
    if (customXAxisMax) {
      updateXAxisMax(datapoint.end ?? datapoint.start);
    }
  };

  const addXAxisStartDatapoint = () => {
    const startDatapoint: SparklineDatapointMetadata = {
      start: new Date(xAxis.min),
      value: Infinity,
      isGap: true,
    };
    sanitizedDatapoints = [startDatapoint].concat(sanitizedDatapoints);
  };

  const addXAxisEndDatapoint = () => {
    const endDatapoint: SparklineDatapointMetadata = {
      start: new Date(xAxis.max),
      value: Infinity,
      isGap: true,
    };
    sanitizedDatapoints = sanitizedDatapoints.concat(endDatapoint);
  };

  for (let i = 0; i < series.datapoints.length; i++) {
    const currentDatapoint = series.datapoints[i];

    if (!isNil(currentDatapoint.value)) {
      if (
        (!customXAxisMin || currentDatapoint.start >= xAxis.min) &&
        (!customXAxisMax || currentDatapoint.start <= xAxis.max)
      ) {
        updateXYBoundaries(currentDatapoint);

        sanitizedDatapoints.push(currentDatapoint);

        if (gapPolicy === 'gap') {
          let nextDatapoint = series.datapoints[i + 1];

          while (
            (isNil(nextDatapoint) || isNil(nextDatapoint.value)) &&
            i < series.datapoints.length
          ) {
            i++;
            nextDatapoint = series.datapoints[i + 1];
          }

          const isGap = getDatapointIsGap(currentDatapoint, nextDatapoint);

          if (isGap) {
            const gapDatapoint = {
              start: currentDatapoint.end ?? currentDatapoint.start,
              end: nextDatapoint.start,
              value: Infinity,
              isGap: true,
            };

            sanitizedDatapoints.push(gapDatapoint);
          }
        }
      }
    }
  }

  if (customXAxisMin && xAxisMin > xAxis.min) {
    addXAxisStartDatapoint();
  }

  if (customXAxisMax && xAxisMax < xAxis.max) {
    addXAxisEndDatapoint();
  }

  return {
    sanitizedSeries: { ...series, datapoints: sanitizedDatapoints },
    domainValues: {
      min: dataMin,
      max: dataMax,
    },
  };
};
